import LibraryElementEnum from 'client/enums/LibraryElementEnum'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import i18n from 'client/i18n'
import { getChildrenEntities } from 'client/reducers/pageReducer'
import { usePage } from 'client/store'
import {
  addStructure,
  removeEntityByIdAndParentId,
} from 'client/store/page/pageActions'
import pageSelectors from 'client/store/page/pageSelectors'
import MenuInterface from 'common/components/entities/Menu/types/MenuInterface'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { LanguageSwitcherInterface } from 'common/types/entities/LanguageSwitcherInterface'
import { RawHtmlInterface } from 'common/types/entities/RawHtmlInterface'
import { TextInterface } from 'common/types/entities/TextInterface'
import { TwitterTweetButtonInterface } from 'common/types/entities/TwitterTweetButtonInterface'
import { useEffect, useRef } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { WebsiteLibraryElementsStructuresCreator } from 'src/editor/pages/website/website-library-elements-structures-creator'
import { useSelectedButtonColor } from 'src/editor/shared/hooks/use-theme-color/use-selected-button-color'
import { createLanguageSwitcher } from 'src/shared/entities/language-switcher/create-language-switcher'
import { createLoginButton } from 'src/shared/entities/login-button/create-login-button'
import { createRawHtml } from 'src/shared/entities/raw-html/create-raw-html'
import { createRegistrationButton } from 'src/shared/entities/registration-button/create-registration-button'
import { createText } from 'src/shared/entities/text/create-text'
import { createTwitterTweetButton } from 'src/shared/entities/twitter-tweet-button/create-twitter-tweet-button'
import { insertChild } from 'src/shared/entities/utils/generate-base-entity'
import { WebsiteHeaderInterface } from 'src/shared/types/website-entities'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import { getDefaultMarginSettings } from '../utils'

interface UseElementProps {
  entity: WebsiteHeaderInterface
}
export const useElement = ({ entity }: UseElementProps) => {
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const dispatch = useAppDispatch()
  const createWebsiteEntity = new WebsiteLibraryElementsStructuresCreator()
    .create

  const fixedT = i18n.getFixedT(pageLocale)
  const registrationButtonStyles = useSelectedButtonColor(
    WebsiteSettingsTypographyTypeEnum.PrimaryButton,
  )
  const loginButtonStyles = useSelectedButtonColor(
    WebsiteSettingsTypographyTypeEnum.SecondaryButton,
  )

  const entities = useSelector(
    //@ts-expect-error @TODO ts migration is needed
    state => getChildrenEntities(state.page.present, entity.childIds),
    shallowEqual,
  )
  const awaitingPostToggleRef = useRef(false)
  const prevEntitiesRef = useRef(entities)
  const menu = entities.find(
    (entity: MenuInterface) => entity.type === EntityTypeEnum.Menu,
  )

  const getToggleValue = (element: LibraryElementEnum): boolean => {
    switch (element) {
      case LibraryElementEnum.LibraryButton:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.Button)
      case LibraryElementEnum.LibraryLoginButton:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.LoginButton)
      case LibraryElementEnum.LibraryRegistrationButton:
        return entities.some(
          //@ts-expect-error
          el => el.type === EntityTypeEnum.RegistrationButton,
        )
      case LibraryElementEnum.LibraryText:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.Text)
      case LibraryElementEnum.LibraryTwitterTweetButton:
        return entities.some(
          //@ts-expect-error
          el => el.type === EntityTypeEnum.TwitterTweetButton,
        )
      case LibraryElementEnum.LibraryRawHTML:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.RawHtml)
      case LibraryElementEnum.LibraryLanguageSwitcher:
        //@ts-expect-error
        return entities.some(el => el.type === EntityTypeEnum.LanguageSwitcher)
      default:
        return false
    }
  }

  const createButtonElement = () => {
    const buttonStructure = createWebsiteEntity({
      libraryElementType: LibraryElementEnum.LibraryWebsiteHeaderButton,
      container: entity,
      locale: pageLocale,
      position: entity.childIds.length,
    })

    dispatch(addStructure(buttonStructure))
  }

  const createLoginButtonElement = () => {
    const button = {
      ...createLoginButton(
        entity.id,
        fixedT('entities.login_button.default_text'),
        '',
      ),
      ...getDefaultMarginSettings(),
    }
    button.backgroundColor = loginButtonStyles.buttonBackgroundColor
    button.border = {
      color: loginButtonStyles.buttonBorderColor,
    }
    button.textColor = loginButtonStyles.buttonTextColor
    button.hover = {
      backgroundColor: loginButtonStyles.hoveredButtonBackgroundColor,
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            button.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [button.id]: button,
      }),
    )
  }

  const createRegistrationButtonElement = () => {
    const button = {
      ...createRegistrationButton(
        entity.id,
        fixedT('entities.registration_button.default_text'),
        '',
      ),
      ...getDefaultMarginSettings(),
    }
    button.backgroundColor = registrationButtonStyles.buttonBackgroundColor
    button.textColor = registrationButtonStyles.buttonTextColor
    button.border = {
      color: registrationButtonStyles.buttonBorderColor,
    }
    button.hover = {
      backgroundColor: registrationButtonStyles.hoveredButtonBackgroundColor,
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            button.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [button.id]: button,
      }),
    )
  }

  const createTextElement = () => {
    const text: TextInterface = createText(
      entity.id,
      undefined,
      fixedT('entity_settings.text.header'),
    )

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            text.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [text.id]: text,
      }),
    )
  }

  const createTwitterTweetButtonElement = () => {
    const button: TwitterTweetButtonInterface = {
      ...createTwitterTweetButton(entity.id),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            button.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [button.id]: button,
      }),
    )
  }

  const createRawHtmlElement = () => {
    const rawHtml: RawHtmlInterface = {
      ...createRawHtml(entity.id),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            rawHtml.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [rawHtml.id]: rawHtml,
      }),
    )
  }

  const createLanguageSwitcherElement = () => {
    const switcher: LanguageSwitcherInterface = {
      ...createLanguageSwitcher(entity.id),
      ...getDefaultMarginSettings(),
    }

    dispatch(
      addStructure({
        [entity.id]: {
          ...entity,
          childIds: insertChild(
            entity.childIds,
            switcher.id,
            entity.childIds.length,
          ),
        } as WebsiteHeaderInterface,
        [switcher.id]: switcher,
      }),
    )
  }

  const removeChildElement = (type: EntityTypeEnum) => {
    const childEntityId = entities.find(
      (entity: { type: EntityTypeEnum }) => entity.type === type,
    )?.id
    if (childEntityId) {
      dispatch(removeEntityByIdAndParentId(childEntityId, entity.id))
    }
  }

  const toggleElement = async (
    value: boolean,
    libraryElement: LibraryElementEnum,
  ) => {
    awaitingPostToggleRef.current = true
    if (value) {
      switch (libraryElement) {
        case LibraryElementEnum.LibraryButton:
          return createButtonElement()
        case LibraryElementEnum.LibraryLoginButton:
          return createLoginButtonElement()
        case LibraryElementEnum.LibraryRegistrationButton:
          return createRegistrationButtonElement()
        case LibraryElementEnum.LibraryText:
          return createTextElement()
        case LibraryElementEnum.LibraryTwitterTweetButton:
          return createTwitterTweetButtonElement()
        case LibraryElementEnum.LibraryRawHTML:
          return createRawHtmlElement()
        case LibraryElementEnum.LibraryLanguageSwitcher:
          return createLanguageSwitcherElement()
        default:
          break
      }
    } else {
      switch (libraryElement) {
        case LibraryElementEnum.LibraryButton:
          return removeChildElement(EntityTypeEnum.Button)
        case LibraryElementEnum.LibraryLoginButton:
          return removeChildElement(EntityTypeEnum.LoginButton)
        case LibraryElementEnum.LibraryRegistrationButton:
          return removeChildElement(EntityTypeEnum.RegistrationButton)
        case LibraryElementEnum.LibraryText:
          return removeChildElement(EntityTypeEnum.Text)
        case LibraryElementEnum.LibraryTwitterTweetButton:
          return removeChildElement(EntityTypeEnum.TwitterTweetButton)
        case LibraryElementEnum.LibraryRawHTML:
          return removeChildElement(EntityTypeEnum.RawHtml)
        case LibraryElementEnum.LibraryLanguageSwitcher:
          return removeChildElement(EntityTypeEnum.LanguageSwitcher)
        default:
          break
      }
    }
  }

  useEffect(() => {
    const prev = prevEntitiesRef.current
    const changed = prev !== entities

    if (!awaitingPostToggleRef.current || !changed) {
      prevEntitiesRef.current = entities
      return
    }

    if (entities.length > 2 && menu.alignSelf === 'flex-end') {
      dispatch(
        addStructure({
          [menu.id]: {
            ...menu,
            alignSelf: 'flex-start',
            mobileAlignSelf: 'flex-start',
          } as MenuInterface,
        }),
      )
    }
    if (entities.length <= 2 && menu.alignSelf === 'flex-start') {
      dispatch(
        addStructure({
          [menu.id]: {
            ...menu,
            alignSelf: 'flex-end',
            mobileAlignSelf: 'flex-end',
          } as MenuInterface,
        }),
      )
    }

    awaitingPostToggleRef.current = false
    prevEntitiesRef.current = entities
  }, [entities, menu.alignSelf])

  return { toggleElement, getToggleValue }
}
